import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { useParams } from 'react-router-dom';

import { formatDate } from 'utils';
import { getBatchUserMappings } from 'services';
import { SimpleSelect } from 'components/CustomSelectFields';

const useStyles = makeStyles(theme=>({
  label: {
    ...theme.typography['h6-medium'],
    color: theme.palette.neutral['clr-900']
  }
}));

function Label(props) {
  const classes = useStyles();
  return <span className={classes.label}>{props.children}&nbsp;</span>;
}

function StudentInfoSkeleton({ activeTab }) {
  return (
    <Box display="flex" alignItems="stretch" gap={2} mt={6}>
      <Skeleton variant="circular" width={90} height={90} />
      
      <Box display='flex' flexDirection='column' gap={2} flexGrow={2} ml={1}>
        <Skeleton variant="text" width={200} height={32} />
        
        <Box display="flex" alignItems="center">
          <Skeleton variant="text" width={60} height={24} />
          <Skeleton variant="text" width={120} height={24} sx={{ ml: 1 }} />
        </Box>
        
        <Box display="flex" alignItems="center">
          <Skeleton variant="text" width={50} height={24} />
          <Skeleton variant="text" width={180} height={24} sx={{ ml: 1 }} />
        </Box>
        
        <Box display="flex" alignItems="center">
          <Skeleton variant="text" width={80} height={24} />
          <Skeleton variant="text" width={150} height={24} sx={{ ml: 1 }} />
        </Box>
      </Box>
      
      {activeTab !== 'interviewWizard' && (
        <Box display='flex' justifyContent='flex-end' flexGrow={1} maxHeight={200} overflow="hidden">
          <Box px={1} pb={1}>
            <Skeleton variant="rectangular" width={200} height={56} sx={{ borderRadius: 1 }} />
          </Box>
        </Box>
      )}
    </Box>
  );
}

function StudentInfo({ student, selectedBatch, setSelectedBatch, activeTab, loading = false }) {
  const { id } = useParams();
  const [batches, setBatches] = React.useState([]);
  const [batchError, setBatchError] = React.useState(false);

  React.useEffect(() => {
    if (id) {
      (async () => {
        try {
          setBatchError(false);
          const { batchUserMappings } = await getBatchUserMappings({
            user: id, include: 'batch'
          });

          setBatches(
            batchUserMappings
              .filter(bum => !bum?.batch?.forPathway)
              .map(bum => ({
                name: bum?.batch?.name,
                _id: bum?.batch?._id,
              }))
          );
        } catch (error) {
          console.error('Error fetching batch mappings:', error);
          setBatchError(true);
          setBatches([]);
        }
      })();
    }
  }, [id]);

  React.useEffect(()=>{
    setSelectedBatch(batches[batches.length-1]?._id || '');
  }, [batches])
    
  if (loading || !student) {
    return <StudentInfoSkeleton activeTab={activeTab} />;
  }
  
  return (
    <Box display="flex" alignItems="stretch" gap={2} mt={6}>
      <Avatar
        src={student?.profileUrl}
        sx={{ width: 90, height: 90, border: '1px solid lightgrey' }}
      >
        {student?.name?.charAt(0) || 'N/A'}
      </Avatar>
      <Box display='flex' flexDirection='column' gap={2} flexGrow={2} ml={1}>
        <Typography variant="h4-medium">{student?.name || 'N/A'}</Typography>
        <Typography variant='h6-medium' color='neutral.clr-600'>
          <Label>Roll No:</Label>{student?._id || 'N/A'}
        </Typography>
        <Typography variant='h6-medium' color='neutral.clr-600'>
          <Label>Email:</Label>{student?.email || 'N/A'}
        </Typography>
        <Typography variant='h6-medium' color='neutral.clr-600'>
          <Label>Last Active:</Label>
          {student?.metadata?.lastActive ?
            formatDate(student?.metadata?.lastActive, 'DD-MMM-YYYY, hh:mma') :
            "N/A"}
        </Typography>
      </Box>
      {activeTab !== 'interviewWizard' && (
      <Box display='flex' justifyContent='flex-end' flexGrow={1} maxHeight={200} overflow="hidden">
        <Box px={1} pb={1}>
          {batchError ? (
            <Typography variant='h6-medium' color='neutral.clr-600' sx={{ pt: 2 }}>
              Batch: N/A
            </Typography>
          ) : (
            <SimpleSelect
              label={'Select batch'}
              options={batches}
              value={selectedBatch}
              disabled={batches.length === 0}
              onChange={(e)=> { setSelectedBatch(e.target.value)}}
            />
          )}
        </Box>
        </Box>
      )}
    </Box>
  );
}

export default StudentInfo;